/**
 * Hooks React Query sur les collections PocketBase.
 * Renvoient des types du domaine (via les mappers) : les composants ne voient
 * jamais de structures PocketBase brutes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProfileFormValues } from '@/components/ProfileForm';
import {
  mapConsumption,
  mapExercise,
  mapFood,
  mapMilestone,
  mapProfile,
  mapWater,
  mapWeight,
} from '@/lib/mappers';
import { currentUserId, pb } from '@/lib/pocketbase';
import type {
  ConsumptionRecord,
  ExerciseEntryRecord,
  FoodFavoriteRecord,
  FoodRecord,
  MilestoneRecord,
  ProfileRecord,
  WaterEntryRecord,
  WeightEntryRecord,
} from '@/types/records';

const keys = {
  profile: ['profile'] as const,
  weights: ['weights'] as const,
  milestones: ['milestones'] as const,
  foods: ['foods'] as const,
  consumptions: ['consumptions'] as const,
  water: ['water'] as const,
  exercises: ['exercises'] as const,
  foodFavorites: ['food-favorites'] as const,
};

/** Profil de l'utilisateur (unique). Renvoie null si l'onboarding n'est pas fait. */
export function useProfile() {
  return useQuery({
    queryKey: keys.profile,
    queryFn: async () => {
      const uid = currentUserId();
      if (!uid) return null;
      try {
        const rec = await pb
          .collection('profiles')
          .getFirstListItem<ProfileRecord>(`user = "${uid}"`);
        return { id: rec.id, profile: mapProfile(rec) };
      } catch {
        return null; // 404 = pas de profil
      }
    },
  });
}

export function useWeights() {
  return useQuery({
    queryKey: keys.weights,
    queryFn: async () => {
      const recs = await pb
        .collection('weight_entries')
        .getFullList<WeightEntryRecord>({ sort: 'date' });
      return recs.map((r) => ({ id: r.id, ...mapWeight(r) }));
    },
  });
}

export function useMilestones() {
  return useQuery({
    queryKey: keys.milestones,
    queryFn: async () => {
      const recs = await pb
        .collection('milestones')
        .getFullList<MilestoneRecord>({ sort: '-target_weight_kg' });
      return recs.map((r) => ({ id: r.id, ...mapMilestone(r) }));
    },
  });
}

export function useFoods() {
  return useQuery({
    queryKey: keys.foods,
    queryFn: async () => {
      const recs = await pb.collection('foods').getFullList<FoodRecord>({ sort: 'name' });
      return recs.map(mapFood);
    },
  });
}

export function useConsumptions() {
  return useQuery({
    queryKey: keys.consumptions,
    queryFn: async () => {
      const recs = await pb
        .collection('consumption_entries')
        .getFullList<ConsumptionRecord>({ sort: 'datetime', expand: 'food' });
      // `foodId`/`foodName` (expand) pour le journal et la saisie rapide ; le type
      // domaine `Consumption` reste agnostique de PocketBase.
      return recs.map((r) => ({
        id: r.id,
        foodId: r.food,
        foodName: r.expand?.food?.name ?? '',
        ...mapConsumption(r),
      }));
    },
  });
}

export function useWater() {
  return useQuery({
    queryKey: keys.water,
    queryFn: async () => {
      const recs = await pb
        .collection('water_entries')
        .getFullList<WaterEntryRecord>({ sort: 'datetime' });
      return recs.map((r) => ({ id: r.id, ...mapWater(r) }));
    },
  });
}

/** Invalide un ou plusieurs caches après mutation. */
function useInvalidate(...keysToInvalidate: readonly (readonly unknown[])[]) {
  const qc = useQueryClient();
  return () => {
    for (const key of keysToInvalidate) void qc.invalidateQueries({ queryKey: key });
  };
}

export function useCreateWeight() {
  const invalidate = useInvalidate(keys.weights);
  return useMutation({
    mutationFn: (data: { date: string; weight_kg: number }) =>
      pb.collection('weight_entries').create({ ...data, user: currentUserId() }),
    onSuccess: invalidate,
  });
}

export function useDeleteWeight() {
  const invalidate = useInvalidate(keys.weights);
  return useMutation({
    mutationFn: (id: string) => pb.collection('weight_entries').delete(id),
    onSuccess: invalidate,
  });
}

export function useUpdateWeight() {
  const invalidate = useInvalidate(keys.weights);
  return useMutation({
    mutationFn: (data: { id: string; date: string; weight_kg: number }) =>
      pb
        .collection('weight_entries')
        .update(data.id, { date: data.date, weight_kg: data.weight_kg }),
    onSuccess: invalidate,
  });
}

export function useCreateFood() {
  const invalidate = useInvalidate(keys.foods);
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      pb.collection('foods').create({ ...data, user: currentUserId(), isPublic: false }),
    onSuccess: invalidate,
  });
}

export function useUpdateFood() {
  // Composition dénormalisée dans les consommations (expand) : éditer un aliment
  // doit aussi rafraîchir les consommations affichées.
  const invalidate = useInvalidate(keys.foods, keys.consumptions);
  return useMutation({
    mutationFn: (data: { id: string; values: Record<string, unknown> }) =>
      pb.collection('foods').update(data.id, data.values),
    onSuccess: invalidate,
  });
}

export function useDeleteFood() {
  const invalidate = useInvalidate(keys.foods, keys.consumptions, keys.foodFavorites);
  return useMutation({
    mutationFn: (id: string) => pb.collection('foods').delete(id),
    onSuccess: invalidate,
  });
}

/** Favoris de l'utilisateur : liste des { favoriteId, foodId } (favoriteId = pour supprimer). */
export function useFoodFavorites() {
  return useQuery({
    queryKey: keys.foodFavorites,
    queryFn: async () => {
      const recs = await pb
        .collection('food_favorites')
        .getFullList<FoodFavoriteRecord>({ sort: '-created' });
      return recs.map((r) => ({ favoriteId: r.id, foodId: r.food }));
    },
  });
}

export function useAddFavorite() {
  const invalidate = useInvalidate(keys.foodFavorites);
  return useMutation({
    mutationFn: (foodId: string) =>
      pb.collection('food_favorites').create({ food: foodId, user: currentUserId() }),
    onSuccess: invalidate,
  });
}

export function useRemoveFavorite() {
  const invalidate = useInvalidate(keys.foodFavorites);
  return useMutation({
    mutationFn: (favoriteId: string) => pb.collection('food_favorites').delete(favoriteId),
    onSuccess: invalidate,
  });
}

export function useCreateConsumption() {
  const invalidate = useInvalidate(keys.consumptions);
  return useMutation({
    mutationFn: (data: { food: string; datetime: string; grams: number; meal?: string }) =>
      pb.collection('consumption_entries').create({ ...data, user: currentUserId() }),
    onSuccess: invalidate,
  });
}

export function useDeleteConsumption() {
  const invalidate = useInvalidate(keys.consumptions);
  return useMutation({
    mutationFn: (id: string) => pb.collection('consumption_entries').delete(id),
    onSuccess: invalidate,
  });
}

export function useUpdateConsumption() {
  const invalidate = useInvalidate(keys.consumptions);
  return useMutation({
    mutationFn: (data: { id: string; grams: number; meal?: string }) =>
      pb.collection('consumption_entries').update(data.id, { grams: data.grams, meal: data.meal }),
    onSuccess: invalidate,
  });
}

export function useCreateWater() {
  const invalidate = useInvalidate(keys.water);
  return useMutation({
    mutationFn: (data: { ml: number; datetime: string }) =>
      pb.collection('water_entries').create({ ...data, user: currentUserId() }),
    onSuccess: invalidate,
  });
}

export function useDeleteWater() {
  const invalidate = useInvalidate(keys.water);
  return useMutation({
    mutationFn: (id: string) => pb.collection('water_entries').delete(id),
    onSuccess: invalidate,
  });
}

export function useExercises() {
  return useQuery({
    queryKey: keys.exercises,
    queryFn: async () => {
      const recs = await pb
        .collection('exercise_entries')
        .getFullList<ExerciseEntryRecord>({ sort: 'datetime' });
      return recs.map((r) => ({ id: r.id, ...mapExercise(r) }));
    },
  });
}

export function useCreateExercise() {
  const invalidate = useInvalidate(keys.exercises);
  return useMutation({
    mutationFn: (data: {
      datetime: string;
      activity: string;
      kcal_burned: number;
      duration_min: number;
      note?: string;
    }) => pb.collection('exercise_entries').create({ ...data, user: currentUserId() }),
    onSuccess: invalidate,
  });
}

export function useDeleteExercise() {
  const invalidate = useInvalidate(keys.exercises);
  return useMutation({
    mutationFn: (id: string) => pb.collection('exercise_entries').delete(id),
    onSuccess: invalidate,
  });
}

export function useUpsertProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id?: string; values: ProfileFormValues }) => {
      const payload = { ...data.values, user: currentUserId() };
      return data.id
        ? pb.collection('profiles').update(data.id, payload)
        : pb.collection('profiles').create(payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.profile }),
  });
}

export function useCreateMilestone() {
  const invalidate = useInvalidate(keys.milestones);
  return useMutation({
    mutationFn: (data: { target_weight_kg: number; label: string }) =>
      pb.collection('milestones').create({ ...data, user: currentUserId() }),
    onSuccess: invalidate,
  });
}

export function useDeleteMilestone() {
  const invalidate = useInvalidate(keys.milestones);
  return useMutation({
    mutationFn: (id: string) => pb.collection('milestones').delete(id),
    onSuccess: invalidate,
  });
}
